import { Vector3 } from '@/math'
import { Position } from './voxelMath'
import type { ChunkedVoxelGrid } from './storage/grid/ChunkedVoxelGrid'

export type Face = 'x+' | 'x-' | 'y+' | 'y-' | 'z+' | 'z-'

/**
 * Stored type names that were renamed. Documents and events saved with the old name load under the
 * new one: the grid palette interns them renamed, and the registry resolves them as aliases.
 */
export const RENAMED_VOXEL_TYPES: Readonly<Record<string, string>> = Object.freeze({
  ink: 'tint',
  // The cube 'snow' bulk material was removed (snow is drawn as soft blob surfaces); its cells keep
  // their colour as solids, which is how plain snow cubes were drawn.
  snow: 'solid',
})

export const VOXEL_MATERIALS = ['soft', 'glossy', 'shiny', 'metallic'] as const
export type VoxelMaterial = typeof VOXEL_MATERIALS[number]
export const DEFAULT_VOXEL_MATERIAL: VoxelMaterial = 'glossy'

export function isVoxelMaterial(value: unknown): value is VoxelMaterial {
  return typeof value === 'string' && (VOXEL_MATERIALS as readonly string[]).includes(value)
}

export type VoxelData = {
  type: string
  face?: Face
  topFace?: Face
  color?: number
  material?: VoxelMaterial
  /**
   * SDF surface density (blob): samples per voxel, an integer in `VOXEL_DENSITY_RANGE`. Absent: the
   * store-wide blob shape (`blobSdfOptionsAtom`), which is how documents saved before it look.
   */
  density?: number
}

/** Allowed `VoxelData.density` values (samples per voxel), inclusive. */
export const VOXEL_DENSITY_RANGE = [1, 6] as const

export function isVoxelDensity(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value >= VOXEL_DENSITY_RANGE[0] && value <= VOXEL_DENSITY_RANGE[1]
}

/**
 * Core voxel types for consistent usage across the codebase
 */

// VoxelMap is a Map where keys are position integers (from posKey) and values are VoxelData.
// VoxelMap is a native Map or a ChunkedVoxelGrid (persistent chunk trie). The document base is
// always a grid (ADR 0003, built only by src/storage/grid/documentBase.ts); native Maps are the
// sparse representation for layers, previews, clips and tests.
export type VoxelMap = Map<number, VoxelData> | ChunkedVoxelGrid

// A voxel with its position
export type PositionedVoxel = Position & VoxelData

// Ray for raycasting
export type Ray = {
  origin: Vector3
  direction: Vector3
}

export type Hit = {
  position: Position
  normal: Vector3
  distance: number
  exactPoint: Vector3  // Exact intersection point on surface
  voxel?: VoxelData
  type: 'voxel' | 'plane'
  mirror?: boolean
  drawingSurfaceId?: string
}

export type Vec3 = { x: number; y: number; z: number }
